import { classes, classNames, Text } from "@alpacahq/ui/old";

import { Disclosure } from "./disclosure";

const columns = [
  {
    header: "Developers",
    rows: [
      { text: "API Docs", href: "https://alpaca.markets/docs/" },
      { text: "Slack", href: "https://alpaca.markets/slack" },
      { text: "Forum", href: "https://forum.alpaca.markets/" },
      { text: "Learn", href: "https://alpaca.markets/learn/" },
      { text: "GitHub", href: "https://github.com/alpacahq" },
      { text: "API Status", href: "http://status.alpaca.markets/" },
    ],
  },
  {
    header: "About Us",
    rows: [
      {
        text: "Story",
        href: "https://techcrunch.com/2021/08/30/alpaca-raises-50m-to-rapidly-scale-its-equities-trading-api-business",
      },
      { text: "Newsletter", href: "https://eepurl.com/g0K5k9" },
      { text: "Blog", href: "https://alpaca.markets/blog/" },
      { text: "Contact", href: "https://alpaca.markets/contact" },
      { text: "We're hiring!", href: "https://alpaca.markets/hiring" },
    ],
  },
  {
    header: "Disclosures",
    rows: [
      {
        text: "Disclosure Library",
        href: "https://alpaca.markets/disclosures",
      },
      {
        text: "Form CRS",
        href: "https://files.alpaca.markets/disclosures/library/FormCRS.pdf",
      },
      {
        text: "Form CRS Responses to Conversion Starters",
        href: "https://files.alpaca.markets/disclosures/library/FormCRSConvStrtrs.pdf",
      },
      {
        text: "Privacy Policy",
        href: "https://s3.amazonaws.com/files.alpaca.markets/disclosures/PrivacyPolicy.pdf",
      },
      {
        text: "Cookie Policy",
        href: "https://s3.amazonaws.com/files.alpaca.markets/disclosures/CookiePolicy.pdf",
      },
      {
        text: "Terms & Conditions",
        href: "https://s3.amazonaws.com/files.alpaca.markets/disclosures/library/TermsAndConditions.pdf",
      },
    ],
  },
];

type SubDisclosureProps = {
  legal?: boolean;
  year: number;
};

export const Footer = ({
  legal,
  cryptoBroker,
  noFadeUp,
}: {
  legal?: boolean;
  cryptoBroker?: string;
  noFadeUp?: boolean;
}) => {
  const year = new Date().getFullYear();

  return (
    <footer className={classes(!noFadeUp && "fade-in-up-medium")}>
      <div className="mx-auto w-full max-w-[1096px] px-8 py-16">
        <div className="mt-[-5px]">
          <div className="flex w-full max-w-full flex-col px-0">
            {!legal && (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                <div className="flex flex-col gap-6 md:flex-row">
                  {columns.map(({ header, rows }) => (
                    <div
                      key={header}
                      className="flex h-full flex-col items-start gap-2"
                    >
                      <h2 className="mb-1 font-semibold text-xs">{header}</h2>
                      {rows.map(({ text, href }) => (
                        <a
                          key={href}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="whitespace-nowrap text-gray-600 text-xs no-underline hover:underline"
                        >
                          {text}
                        </a>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="mt-12 flex items-start justify-start gap-2 md:mt-0 md:justify-end">
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.linkedin.com/company/9300556/"
                  >
                    <i
                      className="fab fa-linkedin-in text-gray-900 text-xl"
                      aria-hidden="true"
                    />
                    <span className="sr-only">LinkedIn</span>
                  </a>
                  <a
                    href="https://github.com/alpacahq"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <i
                      className="fab fa-github text-gray-900 text-xl"
                      aria-hidden="true"
                    />
                    <span className="sr-only">GitHub</span>
                  </a>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://alpaca.markets/slack"
                  >
                    <i
                      className="fab fa-slack text-gray-900 text-xl"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Slack</span>
                  </a>
                </div>
              </div>
            )}
            {cryptoBroker === "BS" ? (
              <AlpacaXCryptoDisclosure year={year} />
            ) : (
              <DefaultDisclosure year={year} />
            )}
          </div>
          <Disclosure cryptoBroker={cryptoBroker}>
            <p className="font-medium text-gray-500 text-xs">
              View important disclosures
            </p>
          </Disclosure>
        </div>
      </div>
    </footer>
  );
};

const DefaultDisclosure = (props: SubDisclosureProps) => {
  const { legal, year } = props;
  return (
    <Text
      className={classNames(
        "mt-0 py-12 text-gray-400 text-xs",
        legal && "pt-12",
      )}
    >
      <CommonSubDisclosure year={year} />
      <br /> <br />
      Cryptocurrency services are made available by Alpaca Crypto LLC
      (&#34;Alpaca Crypto&#34;), a FinCEN registered money services business
      (NMLS # 2160858), and a wholly-owned subsidiary of AlpacaDB, Inc. Alpaca
      Crypto is not a member of SIPC or FINRA. Cryptocurrencies are not stocks
      and your cryptocurrency investments are not protected by either FDIC or
      SIPC. Depending on your location, cryptocurrency services may be provided
      by West Realm Shires Services, Inc. Please see the{" "}
      <a
        href="https://alpaca.markets/disclosures"
        target="_blank"
        className="text-gray-400 underline"
        rel="noopener"
      >
        Disclosure Library
      </a>{" "}
      for more information.
      <br /> <br />
      This is not an offer, solicitation of an offer, or advice to buy or sell
      securities or cryptocurrencies, or open a brokerage account or
      cryptocurrency account in any jurisdiction where Alpaca Securities, Alpaca
      Crypto, are not registered or licensed, as applicable.
    </Text>
  );
};

const AlpacaXCryptoDisclosure = (props: SubDisclosureProps) => {
  const { legal, year } = props;
  return (
    <Text
      className={classNames(
        "mt-0 py-12 text-gray-400 text-xs",
        legal && "pt-12",
      )}
    >
      <CommonSubDisclosure year={year} cryptoBroker="BS" />
      <br /> <br />
      Alpaca Crypto is authorized and regulated by The Securities Commission of
      the Bahamas. Alpaca Crypto is an international business company registered
      in the Commonwealth of The Bahamas, registration number 208648 B. Alpaca
      Crypto is not a member of SIPC or FINRA. Cryptocurrencies are not stocks
      and your cryptocurrency investments are not protected by either FDIC or
      SIPC. Please see the Disclosure Library for more information.
      <br /> <br />
      This is not an offer, solicitation of an offer, or advice to buy or sell
      securities or cryptocurrencies, or open a brokerage account or
      cryptocurrency account in any jurisdiction where Alpaca Securities, Alpaca
      Crypto are not registered or licensed, as applicable. Services offered by
      Alpaca Crypto are not available in the United States and other prohibited
      jurisdictions.
    </Text>
  );
};

const CommonSubDisclosure = ({
  year,
  cryptoBroker,
}: {
  year: number;
  cryptoBroker?: string;
}) => {
  const isBahamas = cryptoBroker === "BS";
  return (
    <>
      © {year} AlpacaDB, Inc. All rights reserved.
      <br />© {year} Alpaca Securities LLC All rights reserved.
      <br />© {year} {isBahamas ? "AlpacaX" : "Alpaca"} Crypto LLC All rights
      reserved.
      <br /> <br />
      Securities Brokerage services are provided by Alpaca Securities , member{" "}
      <a
        href="https://www.finra.org/"
        target="_blank"
        className="text-gray-400 underline"
        rel="noopener"
      >
        FINRA
      </a>
      /
      <a
        href="https://www.sipc.org/"
        target="_blank"
        className="text-gray-400 underline"
        rel="noopener"
      >
        SIPC
      </a>
      ,a wholly-owned subsidiary of AlpacaDB, Inc. Technology and services are
      offered by AlpacaDB, Inc.
    </>
  );
};

export default Footer;
